import { Button, LinearProgress, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { MemberUpdateActionEnum } from 'src/shared/models/enums/memberUpdateAction.enum';
import { createMemberUpdate } from 'src/shared/services/memberUpdateService';
import { MemberUpdateRequest } from '../models/memberUpdateRequest';

// Uppdateringarna skickas in i omgångar. Varje rad kostar flera databasanrop på servern, så ett
// enda anrop med tiotusentals rader skulle hinna tajma ut innan det blev klart.
const CHUNK_SIZE = 100;

type MemberUpdateApproveAllProps = {
  memberUpdateIds: number[];
  onClose: () => void;
  onDone: () => void;
};

export const MemberUpdateApproveAll = (props: MemberUpdateApproveAllProps) => {
  const { memberUpdateIds, onClose, onDone } = props;
  const { t } = useTranslation(['common', 'member']);
  const isMounted = useIsMounted();

  const [running, setRunning] = useState<boolean>(false);
  const [done, setDone] = useState<number>(0);
  const abortRequested = useRef<boolean>(false);

  const total = memberUpdateIds.length;

  const approveAll = async () => {
    setRunning(true);
    abortRequested.current = false;

    let processed = 0;

    try {
      for (let i = 0; i < memberUpdateIds.length; i += CHUNK_SIZE) {
        if (abortRequested.current) break;

        const chunk: MemberUpdateRequest[] = memberUpdateIds.slice(i, i + CHUNK_SIZE).map((id) => ({
          memberUpdate_id: id,
          action: MemberUpdateActionEnum.Accept,
        }));

        // Omgångarna måste gå i tur och ordning, servern skriver mot samma medlemstabeller.
        // eslint-disable-next-line no-await-in-loop
        await createMemberUpdate(chunk);

        processed += chunk.length;

        if (!isMounted()) return;

        setDone(processed);
      }

      if (isMounted()) {
        if (abortRequested.current && processed < total)
          toast.info(t('member:memberUpdate.approveAllInterrupted', { done: processed, total }) as string);
        else toast.success(t('member:memberUpdate.approveAllDone', { count: processed }) as string);
      }
    } catch (e) {
      ConsoleHelper.error(e);

      // Interceptorn visar felet. Det som hann godkännas är redan skrivet, resten ligger kvar.
      if (isMounted() && processed > 0) toast.info(t('member:memberUpdate.approveAllInterrupted', { done: processed, total }) as string);
    } finally {
      if (isMounted()) {
        setRunning(false);
        onDone();
      }
    }
  };

  return (
    <GenericDialog
      open
      maxWidth='sm'
      showDividers={false}
      disableBackdropClosing
      dialogTitle={t('member:memberUpdate.approveAllTitle')}
      onClose={() => {
        // Stäng inte medan omgångarna körs, då tappar vi förloppet ur sikte.
        if (!running) onClose();
      }}
      onSave={approveAll}
      dialogContent={
        <Box>
          <Typography variant='body1'>
            {running ? t('member:memberUpdate.approveAllProgress', { done, total }) : t('member:memberUpdate.approveAllMessage', { count: total })}
          </Typography>
          {running && (
            <LinearProgress variant='determinate' value={total > 0 ? (done / total) * 100 : 0} sx={{ mt: 2, height: 8, borderRadius: 1 }} />
          )}
        </Box>
      }
      dialogActions={
        running ? (
          <Button
            variant='outlined'
            color='primary'
            disableElevation
            onClick={() => {
              abortRequested.current = true;
            }}
          >
            {t('common:button.abort')}
          </Button>
        ) : (
          <>
            <Button variant='outlined' color='primary' disableElevation onClick={onClose}>
              {t('common:button.cancel')}
            </Button>
            <Button autoFocus variant='contained' color='primary' disableElevation onClick={approveAll}>
              {t('member:memberUpdate.approveAll')}
            </Button>
          </>
        )
      }
    />
  );
};
